// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
// ── Platform surface ids, collections, tag and owner type ──────────────────
/**
 * The `register_surface` values the internal register templates declare. Each
 * template carries the vocabularies of one register as select options; the
 * templates are register-only and pack-locked (`PACK_LOCKED_REGISTER_SURFACES`,
 * `REGISTER_ONLY_SURFACES`) the day they are declared (62.50.0 lesson 1).
 */
export const CUSTOMS_TAX_REGISTER_SURFACES = {
    /** A dossier the organisation files: `procedure` (options name a `procedure_id` and a `register_role`), `confirmation_purpose`, `confirmation_answer`. */
    dossier: 'customs_tax_dossier',
    /** A document received from the authority: `document_kind` (options name the `authority_generated` templates they admit and their `received_role`). */
    receivedDocument: 'customs_tax_received_document',
    /** A payment: `payer_class`, `payment_date_basis`. */
    payment: 'customs_tax_payment',
    /** An anchor event against a customs record: `anchor_kind` (each option names the clock it starts, `clock_deadline_id`). */
    anchorEvent: 'customs_tax_anchor_event',
    /** A duty-free list: `party_role`, `stop_use_event` (options may raise the declare-and-pay duty). */
    dutyFreeList: 'customs_tax_duty_free_list',
    /** A usage notification: `notice_kind` (options name a `schedule_rule`, their clocks and the templates they are recorded on). */
    usageNotification: 'customs_tax_usage_notification',
    /** A refund record: `refund_classification` (options name a `classification_role`). */
    refundRecord: 'customs_tax_refund_record',
    /** A debt instrument: `instrument_kind` (`instrument_role`, admitted templates), `instalment_band` (`clock_deadline_id`). */
    debtInstrument: 'customs_tax_debt_instrument',
    /** An examination at the premises: `examination_purpose`, `step_kind` (`step_role`, admitted templates). */
    examination: 'customs_tax_examination',
    /** A project transfer: `transfer_role` (`party_side`). */
    projectTransfer: 'customs_tax_project_transfer',
};
/** The collections the registers persist to, keyed by record family. Platform names. */
export const CUSTOMS_TAX_COLLECTIONS = {
    dossiers: 'grc_customs_tax_dossiers',
    receivedDocuments: 'grc_customs_tax_received_documents',
    payments: 'grc_customs_tax_payments',
    anchorEvents: 'grc_customs_tax_anchor_events',
    differenceClosures: 'grc_customs_tax_difference_closures',
    /** Gap R-2 (owner, 2026-10-26): a person's closure of a difference between a dossier's figure and a received document's. */
    dossierFigureClosures: 'grc_customs_tax_dossier_figure_closures',
    dutyFreeLists: 'grc_customs_tax_duty_free_lists',
    listAmendments: 'grc_customs_tax_list_amendments',
    listStopEvents: 'grc_customs_tax_list_stop_events',
    deductions: 'grc_customs_tax_deductions',
    usageNotifications: 'grc_customs_tax_usage_notifications',
    refundRecords: 'grc_customs_tax_refund_records',
    debtInstruments: 'grc_customs_tax_debt_instruments',
    examinations: 'grc_customs_tax_examinations',
    examinationSteps: 'grc_customs_tax_examination_steps',
    officeExaminations: 'grc_customs_tax_office_examinations',
    projectTransfers: 'grc_customs_tax_project_transfers',
    ruleDeterminations: 'grc_customs_tax_rule_determinations',
};
/** The one guidance tag every page renders through `BoundarySection` (the platform-boundary guidance carries it). */
export const CUSTOMS_TAX_BOUNDARY_TAG = 'customs_tax_administration_boundary';
/** The attachment owner type every file of these registers is uploaded under, then bound server-side. */
export const CUSTOMS_TAX_EVIDENCE_OWNER_TYPE = 'customs_tax_administration_evidence';
// ── Register field ids a pack 1.16.1 template declares (platform keys) ─────
/**
 * Pack 1.16.1 (62.55.0 close-out walk P-3) — the transfer template's select
 * stating which case of the transferee's new-declaration duty a project is;
 * each option names, by `clock_deadline_id`, the one clock that case runs.
 */
export const CTA_TRANSFER_CASE_FIELD = 'transfer_case';
/**
 * Pack 1.16.1 (legal D-4) — the examination template's select recording the
 * examination's basis as the decision states it; only the option that keeps a
 * gated clock names it (`clock_deadline_id`).
 */
export const CTA_EXAMINATION_BASIS_FIELD = 'examination_basis';
/**
 * Pack 1.16.1 (P-2) — the examination dates a clock of these roles may name as
 * its `register_clock_anchor`. A clock that names none runs from its role's
 * default date (signing for delivery and announcement, the announcement for
 * the duration, the period end for the conclusion).
 */
export const CTA_EXAMINATION_CLOCK_ANCHOR_FIELDS = {
    examination_decision_delivery: ['decision_signed_on', 'announced_on'],
    examination_conclusion: ['expert_opinion_requested_on', 'expert_opinion_reply_received_on'],
};
// ── Pack attribute names (KEYS a pack may author, never values) ────────────
export const CTA_PACK_ATTR = {
    // Option level, on the register templates.
    /** Dossier `procedure` option: the installed procedure record it stands for. */
    procedureId: 'procedure_id',
    /** Dossier `procedure` option: what the register does with dossiers of it (`CTA_DOSSIER_REGISTER_ROLES`). */
    registerRole: 'register_role',
    /** Received-kind / step / instrument option: the `authority_generated` template ids (same pack) it admits. */
    admitsTemplateIds: 'admits_template_ids',
    /** Received-kind option: what the received document is to its dossier (`CTA_RECEIVED_ROLES`). */
    receivedRole: 'received_role',
    /** Received-kind option: receiving it starts the taxpayer's reply clock of the dossier's procedure. */
    startsReplyClock: 'starts_reply_clock',
    /**
     * Pack 1.16.4 (OQ-86-4) — received-kind option: the one record family a
     * document of this kind must be bound to (`CTA_RECEIVED_BINDING_KINDS`). A
     * kind that declares none may bind to any. The coercive-enforcement notices
     * declare `debt_instrument`.
     */
    requiredBindingKind: 'required_binding_kind',
    /**
     * Pack 1.16.4 (legal D-8) — examination purpose option: choosing it requires
     * `re_examination_of` to name an earlier examination of this organisation.
     * Boolean; read as `raw[flag] === true`.
     */
    requiresEarlierExamination: 'requires_earlier_examination',
    /**
     * Pack 1.16.4 (legal L4-5, owner decision 2026-10-26) — enforcement-notice
     * kind: a document of this kind may be recorded as received by the
     * organisation as a third party, bound to no record of its own
     * (`bound_to.kind` `received_as_third_party`). Boolean; read as
     * `raw[flag] === true`. A kind without it keeps its `required_binding_kind`.
     */
    receivableAsThirdParty: 'receivable_as_third_party',
    /** Any option: the installed deadline whose clock it starts (validated by `FORM_OPTION_CLOCK_DEADLINE_UNKNOWN`, shared with 62.50.0). */
    clockDeadlineId: 'clock_deadline_id',
    /** Usage notice option (three-yearly): the cycle clock (years) the notice window follows. */
    cycleClockDeadlineId: 'cycle_clock_deadline_id',
    /** Usage notice option: its schedule rule (`CTA_USAGE_SCHEDULE_RULES`). */
    scheduleRule: 'schedule_rule',
    /** Usage notice option: the applicant templates a notification of this kind is recorded on. */
    recordsOnTemplateIds: 'records_on_template_ids',
    /** Usage notice option: the clause the duty rests on, as the pack cites it (shown verbatim). */
    clauseCitation: 'clause_citation',
    /** Confirmation purpose option: a dissolution or closure purpose (AC-A7). */
    closurePurpose: 'closure_purpose',
    /** Confirmation answer option: `CTA_ANSWER_OUTCOMES`. */
    answerOutcome: 'answer_outcome',
    /** Stop-use event option: it raises the duty to declare and pay as a taxpayer item (AC-C2). */
    raisesDeclareAndPayDuty: 'raises_declare_and_pay_duty',
    /** Examination step option: `CTA_EXAMINATION_STEP_ROLES`. */
    stepRole: 'step_role',
    /** Examination purpose option: `CTA_EXAMINATION_PURPOSES`. */
    purposeRole: 'purpose_role',
    /** Debt instrument option: `CTA_DEBT_INSTRUMENT_ROLES`. */
    instrumentRole: 'instrument_role',
    /** Transfer role option: `CTA_TRANSFER_SIDES`. */
    partySide: 'party_side',
    /** Refund classification option: `CTA_REFUND_CLASSIFICATIONS`. */
    classificationRole: 'classification_role',
    // Template level, on the instrument's form templates.
    /** An `authority_generated` template's declared taxpayer use for named procedures (AC-A6, LQ-3). */
    taxpayerUse: 'taxpayer_use',
    // Field level.
    /** A field that carries an individual's data (AC-F3). Boolean. */
    personalData: 'personal_data',
    /**
     * OQ-86-3 wiring (2026-10-27) — debt-instrument template field: the
     * instrument roles ({@link CTA_STATED_CASE_INSTRUMENT_ROLES}) whose decision
     * states a case this field's options name. The register offers the options as
     * the stated case's choice on an instrument of those roles, beside the case in
     * the decision's own words (D-1). Recorded as stated; never evaluated.
     */
    statedCaseRoles: 'stated_case_roles',
    /**
     * OQ-86-3 wiring (2026-10-27) — refund-record template field, boolean: this
     * field's options are the cases a classification notice states, each naming
     * its `classification_role` and the `authority_refund_decision` clock it
     * starts (`clock_deadline_id`). The register offers them as the stated
     * ground of the classification and reads the handling clocks through them.
     */
    classificationCase: 'classification_case',
    // Deadline-record level.
    /** The procedure the clock belongs to (the spec's link; `null` on all of pack 1.15.1). */
    linkedProcedureId: 'linked_procedure_id',
    /** What the register does with the clock (`CTA_CLOCK_ROLES`). `sla_type` stays display-only. */
    registerClockRole: 'register_clock_role',
    /**
     * The record date the clock runs from, where its role offers more than one
     * (a transfer's `certificate_adjusted_on` / `contract_on`; pack 1.16.1, an
     * examination's {@link CTA_EXAMINATION_CLOCK_ANCHOR_FIELDS}).
     */
    registerClockAnchor: 'register_clock_anchor',
    /** Which way the window runs from its anchor (`LICENCE_WINDOW_DIRECTIONS`: `after`, `before`); absent is `after`. */
    windowDirection: 'window_direction',
    // Submission-channel level, on the framework record.
    /** The channel is a reference to a document already held on a connected system. Boolean. */
    refersToHeldDocument: 'refers_to_held_document',
    /** The rule that the referred document is not filed again, as the pack words it. Required with the flag. */
    noRefileRule: 'no_refile_rule',
    // Framework-record level.
    /** `obligation_owner` value → `CtaObligationSide`. A value the map lacks raises nothing. */
    obligationOwnerSides: 'obligation_owner_sides',
    /** The transition declaration ({@link CtaTransitionRuleDeclaration}). */
    transitionRule: 'transition_rule',
    /** The effective-dated timeline (1.15.1's key, extended entry by entry; {@link CtaTimelineEntryDeclaration}). */
    amendmentTimeline: 'amendment_timeline',
    /** Clause-level relationships ({@link CtaClauseRelationshipDeclaration}). */
    clauseRelationships: 'clause_relationships',
    /** The usage schedule's anchor and counting declarations ({@link CtaUsageScheduleDeclaration}). */
    usageScheduleRule: 'usage_schedule_rule',
    /**
     * Cross-references the signed text prints that the pack records as odd (LQ-2): each quoted
     * as printed, with its unit, page and note ({@link CtaPrintedCrossReference}). No link rests on one.
     */
    printedCrossReferenceOddities: 'printed_cross_reference_oddities',
    // Article-map level (articles.json).
    /** The khoản (and điểm) units an article holds, so a relationship naming one can be checked. */
    clauses: 'clauses',
};
// ── Platform vocabularies (what a register does; never an instrument's values) ──
/** What the register does with dossiers of a procedure. */
export const CTA_DOSSIER_REGISTER_ROLES = [
    'general',
    'duty_free_list',
    'usage_notification',
    'refund',
    /**
     * Pack 1.16.4 (R-1) — an overpaid amount's dossier: it
     * carries a refund record (decision, offset, recovery and a self-discovered
     * wrongful refund) with none of the duty refund's classification, materials
     * report, norms or refund examinations, which the text gives that procedure only.
     */
    'overpaid_refund',
    'debt_freeze',
    'debt_write_off',
    'instalment',
    'fulfilment_confirmation',
    'project_transfer',
];
/** What a received document is to the dossier, record or payment it binds to. */
export const CTA_RECEIVED_ROLES = [
    'acknowledgement',
    'supplement_request',
    'refusal',
    'not_exempt_answer',
    'decision',
    'assessment_decision',
    'assessment_amendment',
    'fulfilment_answer',
    'deduction_sheet',
    'notice',
    /**
     * Pack 1.16.4 (OQ-86-4) — a coercive-enforcement notice, decision or letter
     * received and recorded against the debt record it concerns. Recorded only:
     * nothing reads a consequence, a measure or a date out of it.
     */
    'enforcement_notice',
];
/**
 * The record families a received document may be bound to — the `kind` of its
 * `bound_to`. A received kind may name one (`required_binding_kind`, pack
 * 1.16.4); the register then refuses any other.
 */
export const CTA_RECEIVED_BINDING_KINDS = [
    'dossier',
    'customs_record',
    'payment',
    'examination',
    'debt_instrument',
];
/** A confirmation-of-fulfilment answer as received (AC-A7). */
export const CTA_ANSWER_OUTCOMES = ['fulfilled', 'not_fulfilled', 'supplement'];
/**
 * What the register does with an installed clock. Read from the deadline's
 * `register_clock_role`; a clock with none is listed as declared and drives
 * nothing. `sla_type` is display-only and never branched on (R-46, the
 * sector-licensing precedent).
 */
export const CTA_CLOCK_ROLES = [
    'taxpayer_reply',
    'authority_intake',
    'authority_decision',
    'payment_due_from_anchor',
    'payment_due_stated_by_decision',
    'usage_annual_notice',
    'usage_triennial_cycle',
    'usage_triennial_notice',
    'usage_five_year_period',
    'usage_five_year_new_declaration',
    'usage_completion_notice',
    'instalment_maximum_period',
    'instalment_notice',
    'examination_decision_delivery',
    'examination_announcement',
    'examination_duration',
    'examination_extension',
    'examination_conclusion',
    'transfer_new_declaration',
    'transition_deadline',
    'authority_refund_decision',
    /**
     * OQ-86-3 wiring (2026-10-27) — the period no instalment plan may exceed,
     * counted from the written acceptance (`approved_on`), whatever band was
     * picked. The end of a period, never an overdue item; the plan's stated end
     * is the earlier of it and the guarantee's validity as the decision states it.
     */
    'instalment_period_ceiling',
    /**
     * OQ-86-3 wiring (2026-10-27) — the authority's enforcement threshold,
     * counted from a notified debt's due date as the notice states it
     * (`stated_due_on`). Shown as the authority's clock; never the
     * organisation's overdue item, and no interest is computed.
     */
    'authority_enforcement_from_due',
    /**
     * OQ-86-3 wiring (2026-10-27) — the authority's threshold for enforcing
     * against the guarantor of an instalment plan, counted from the due date the
     * written acceptance states (`stated_due_on` on the instalment record).
     */
    'authority_guarantor_enforcement_from_due',
];
/**
 * Decree 252/2026 intake (S4, 2026-10-26) — the refund record dates an
 * `authority_refund_decision` clock may name as its `register_clock_anchor`,
 * typed against the record so a rename fails to compile. The clock is the
 * authority's time to decide a refund dossier, and the instrument counts it
 * from a different act for each classification: from the acceptance
 * (classification) notice the record holds as a received document, or from
 * the conclusion of the pre-refund examination at the premises the record
 * links. A clock of this role must name one (`DEADLINE_CLOCK_ANCHOR_INVALID`):
 * the register offers both dates and no default.
 */
export const CTA_REFUND_CLOCK_ANCHOR_FIELDS = [
    'classification_document_id',
    'pre_refund_examination_id',
];
/** How a usage-notification duty falls due (AC-C4, AC-G3). Annual and three-yearly are never derived from each other. */
export const CTA_USAGE_SCHEDULE_RULES = [
    'annual_while_importing',
    'triennial_from_anchor',
    'five_year_period',
    'five_year_new_declaration',
    'completion_notice',
];
/** The three-yearly anchor rules the pack may declare (AC-G3). */
export const CTA_TRIENNIAL_ANCHOR_RULES = [
    'year_imports_ended_or_list_expired',
    'financial_year_containing_commencement',
];
/**
 * Whether the anchor year counts as year one of the three (LQ-4). While the pack
 * declares `pending_legal_reading`, the schedule shows both candidates and
 * computes no single date. Pack 1.16.4 declares `anchor_year_counts_as_first`
 * (owner decision 2026-10-26, the protective reading) and records the other
 * reading as `noted_alternative_convention`: shown as a note, never a date.
 */
export const CTA_TRIENNIAL_COUNTING_CONVENTIONS = [
    'pending_legal_reading',
    'anchor_year_counts_as_first',
    'cycle_counts_from_year_after_anchor',
];
export const CTA_EXAMINATION_STEP_ROLES = [
    'decision',
    'preparation_notice',
    'announcement_minutes',
    'minutes',
    'extension',
    'suspension',
    'conclusion',
    'post_conclusion_decision',
];
export const CTA_EXAMINATION_PURPOSES = ['general', 'pre_refund', 'post_refund', 'usage'];
export const CTA_DEBT_INSTRUMENT_ROLES = [
    'freeze',
    'freeze_end',
    'write_off',
    'restoration',
    'instalment_request',
    'instalment_notice',
    /**
     * Pack 1.16.4 (OQ-86-4) — the tax debt as the authority notified it, recorded
     * so that the enforcement notices received about it have a debt record to be
     * bound to. A record of what was notified; nothing is computed from it.
     */
    'notified_debt',
];
/**
 * The instrument roles whose decision names the case it rests on (D-1, pack
 * 1.16.4: a freeze and a write-off; OQ-86-3 wiring, 2026-10-27: the end of a
 * freeze, whose decision states the ground that ends it). Only these carry a
 * `stated_case`, and a template field's `stated_case_roles` names only these.
 */
export const CTA_STATED_CASE_INSTRUMENT_ROLES = [
    'freeze',
    'freeze_end',
    'write_off',
];
/**
 * OQ-86-3 wiring (2026-10-27) — the instrument roles whose notice or written
 * acceptance states a due date (`stated_due_on`): the notified debt (the
 * authority's enforcement threshold runs from it) and the instalment request
 * or notice (the guarantor's threshold runs from the acceptance's due date).
 */
export const CTA_STATED_DUE_INSTRUMENT_ROLES = [
    'notified_debt',
    'instalment_request',
    'instalment_notice',
];
export const CTA_TRANSFER_SIDES = ['transferor', 'transferee'];
export const CTA_REFUND_CLASSIFICATIONS = ['refund_first', 'examination_first'];
/** Clause-level relationship kinds (AC-G2). */
export const CTA_CLAUSE_RELATIONSHIP_KINDS = [
    'overlap',
    'amendment',
    'repeal',
    'supersession',
];
/** A declared reading's state. `provisional_pending_verification` is shown on every reading that uses it (AC-G7). */
export const CTA_READING_STATES = ['settled', 'provisional_pending_verification'];
/** The date a record's governing rules are read from (Slice E). */
export const CTA_GOVERNING_DATE_KINDS = ['filing_date', 'decision_date'];
/** The test the pack declares for an examination under way at commencement. */
export const CTA_EXAMINATION_IN_PROGRESS_TESTS = ['announced_before_commencement'];
/** Which rules govern a record, derived on read. */
export const CTA_GOVERNING_OUTCOMES = ['earlier_rules', 'current_rules', 'no_reading'];
/** A provision unit's state on a date, read from the clause-level relationships. */
export const CTA_PROVISION_STATES = [
    'in_force',
    'amended',
    'repealed',
    'not_yet_in_force',
];
/** The record families the evidence-pack collector, the exports and the transition statement cover. */
export const CTA_RECORD_TYPES = [
    'dossier',
    'received_document',
    'payment',
    'anchor_event',
    'duty_free_list',
    'deduction',
    'usage_notification',
    'refund_record',
    'debt_instrument',
    'examination',
    'office_examination',
    'project_transfer',
    'rule_determination',
];
/** The five UC-VN-96 exports plus UC-VN-97's transition statement (AC-F2). Formats go through `UnifiedExportService`. */
export const CTA_EXPORT_KINDS = [
    'dossiers',
    'payments',
    'duty_free_lists',
    'examinations',
    'debt_instruments',
    'transition_statement',
];
/**
 * The install mode a record was made in, as one word (AC-A9). Records of this
 * module carry two spellings of the same answer — the install-mode policy's
 * reason (`direct_install`, `reference_install`) and the installed mode
 * (`direct`, `reference`) — so every surface and export reads them through this
 * one mapping (62.55.0 close-out walk: pages printed "Made in direct_install
 * mode"). Anything else (`not_installed`, `superseded`, …) is returned as null.
 */
export function ctaInstallModeWord(mode) {
    if (mode === 'direct' || mode === 'direct_install')
        return 'direct';
    if (mode === 'reference' || mode === 'reference_install')
        return 'reference';
    return null;
}
/** Why a governing-rules reading has no outcome (`CtaGoverningRulesReading.no_reading_reason`). */
export const CTA_NO_READING_REASONS = [
    'no_transition_rule',
    'kind_not_listed',
    'governing_date_missing',
    'announcement_missing',
];
/**
 * Field-name fragments AC-A8's source guard refuses on any stored or returned
 * field of this module. Held here so the guard and this file cannot drift.
 */
export const CTA_FORBIDDEN_AMOUNT_FIELD_FRAGMENTS = [
    'computed_',
    'calculated_',
    'total_',
    'owed',
    'due_amount',
    'interest_amount',
];
/** Every amount field of this module begins with this prefix (AC-A8). */
export const CTA_AMOUNT_FIELD_PREFIX = 'stated_';
export const CUSTOMS_TAX_ERROR_CODES = {
    /** 404: a record of this module, or one of another organisation (the same response). */
    recordNotFound: 'CTA_RECORD_NOT_FOUND',
    /** 404: a customs record that does not resolve in this organisation (another organisation's id reads the same). */
    customsRecordNotFound: 'CTA_CUSTOMS_RECORD_NOT_FOUND',
    /** 404: an Authority Request that does not resolve in this organisation. */
    authorityRequestNotFound: 'CTA_AUTHORITY_REQUEST_NOT_FOUND',
    /**
     * 404: a requirement the named framework, installed in this organisation, does
     * not carry (an uninstalled framework reads the same). Before the 62.55.0
     * close-out walk (O3) an unknown requirement answered 200 `current_version_only`,
     * a reading of something that does not exist.
     */
    requirementNotFound: 'CTA_REQUIREMENT_NOT_FOUND',
    /** 409: no operational install declares the surface (reference or not installed); `details.absence`. */
    registerNotDeclared: 'CTA_REGISTER_NOT_DECLARED',
    /** 422: a value the installed template does not declare among the field's options. */
    optionNotDeclared: 'CTA_OPTION_NOT_DECLARED',
    /** 422: a procedure no installed dossier option names. */
    procedureNotDeclared: 'CTA_PROCEDURE_NOT_DECLARED',
    /** 422: a template that is not one of the procedure's related templates. */
    templateNotForProcedure: 'CTA_TEMPLATE_NOT_FOR_PROCEDURE',
    /** 422: a template `isFileableFilingForm` refuses and no read taxpayer use admits. */
    templateNotFileable: 'CTA_TEMPLATE_NOT_FILEABLE',
    /** 422: a received template the kind does not admit, or one that is not `authority_generated`. */
    receivedTemplateNotAdmitted: 'CTA_RECEIVED_TEMPLATE_NOT_ADMITTED',
    /**
     * 422 (pack 1.16.4, OQ-86-4): a received kind that declares the record family
     * it binds to (`required_binding_kind`) bound to another; `details.required`.
     */
    receivedBindingKindRequired: 'CTA_RECEIVED_BINDING_KIND_REQUIRED',
    /**
     * 422 (pack 1.16.4, legal L4-5): a document recorded as received as a third
     * party whose kind does not declare `receivable_as_third_party`.
     */
    thirdPartyReceiptNotDeclared: 'CTA_THIRD_PARTY_RECEIPT_NOT_DECLARED',
    /**
     * 422 (pack 1.16.4, legal D-8): an examination purpose that declares
     * `requires_earlier_examination` with no `re_examination_of`.
     */
    earlierExaminationRequired: 'CTA_EARLIER_EXAMINATION_REQUIRED',
    /**
     * 422 (pack 1.16.4, D-1): a `stated_case` with no decision document as its
     * source, or on an instrument that is not a freeze or a write-off.
     */
    statedCaseInvalid: 'CTA_STATED_CASE_INVALID',
    /** 422: a channel code the framework does not declare; `details.state` is the discovery state. */
    channelNotDeclared: 'CTA_CHANNEL_NOT_DECLARED',
    /** 422: a held-document channel with no held-document reference. */
    heldDocumentReferenceRequired: 'CTA_HELD_DOCUMENT_REFERENCE_REQUIRED',
    /** 422: a dossier with no customs record. */
    customsRecordRequired: 'CTA_CUSTOMS_RECORD_REQUIRED',
    /** 422: a stated figure with no source, or a figure on a field that is not `stated_`. */
    figureSourceRequired: 'CTA_FIGURE_SOURCE_REQUIRED',
    /** 422: a record bound to more or fewer than one subject. */
    bindingInvalid: 'CTA_BINDING_INVALID',
    /** 422: a reference to a record of another register kind (`details.reason`). */
    kindMismatch: 'CTA_KIND_MISMATCH',
    /** 422: dates out of order (`details.reason`, `details.field`). */
    dateOrderInvalid: 'CTA_DATE_ORDER_INVALID',
    /** 422: closing a difference with no reason, or for figures that do not differ. */
    differenceClosureInvalid: 'CTA_DIFFERENCE_CLOSURE_INVALID',
    /** 403: the norms confirmation without the manufacturing grant. */
    normsConfirmationGrantRequired: 'CTA_NORMS_CONFIRMATION_GRANT_REQUIRED',
    /** 422: a determination with no reason. */
    determinationReasonRequired: 'CTA_DETERMINATION_REASON_REQUIRED',
    /** 422: an anchor kind whose declared clock is not installed or not live. */
    anchorClockUnavailable: 'CTA_ANCHOR_CLOCK_UNAVAILABLE',
    /** 409: a stale save; the page offers a reload. */
    versionConflict: 'CTA_VERSION_CONFLICT',
    /** 404: a user id not in this organisation's directory. */
    userNotFound: 'CTA_USER_NOT_FOUND',
    /** 400: a surface key this module does not serve. */
    surfaceUnknown: 'CTA_SURFACE_UNKNOWN',
    /** 422: an export kind or format outside the declared ones. */
    exportKindUnknown: 'CTA_EXPORT_KIND_UNKNOWN',
};
//# sourceMappingURL=customs-tax-administration.js.map